import { CalendarOutlined, ExportOutlined, LogoutOutlined, PlusOutlined, SettingOutlined, TeamOutlined, UserOutlined, WalletOutlined } from '@ant-design/icons';
import { App as AntApp, Button, Card, Dropdown, Form, Layout, Space, Tabs, Tooltip, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AccountPrefs, DayResult, Job, JobFlight, JobSummary, SearchRequestBody, WalletBalance } from '../../src/types.ts';
import { AddFundsModal } from './components/AddFundsModal.tsx';
import { BookingDrawer, type BookingTarget } from './components/BookingDrawer.tsx';
import { DayDrawer } from './components/DayDrawer.tsx';
import { LoginModal } from './components/LoginModal.tsx';
import { RecentSearches, summaryTitle } from './components/RecentSearches.tsx';
import { ResultsPanel, type SearchMeta } from './components/ResultsPanel.tsx';
import { SettingsDrawer } from './components/SettingsDrawer.tsx';
import { CABIN_LABELS, SearchForm, type SearchFormValues } from './components/SearchForm.tsx';
import { TransferModal } from './components/TransferModal.tsx';
import { WafBanner } from './components/WafBanner.tsx';
import { api, ApiRequestError, errorText, type AuthStatus, type WafStatus } from './lib/api.ts';
import { airportLabel, fromPref, rawAirportList, toAirportOption, toPrefInput, type AirportOption } from './lib/airports.ts';
import type { MaxStops } from './lib/flights.ts';
import { fmtMoney, MONTHS } from './lib/format.ts';
import { BookingsPage } from './pages/BookingsPage.tsx';
import { PassengersPage } from './pages/PassengersPage.tsx';
import { WalletPage } from './pages/WalletPage.tsx';

const POLL_MS = 1500;

type View = 'search' | 'bookings' | 'wallet' | 'passengers';

/** True below the tablet breakpoint: the search form then stacks above the results instead of living in a side panel. */
function useNarrow(query = '(max-width: 991px)'): boolean {
  const [narrow, setNarrow] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return narrow;
}

function paxLabel(adults: number, children = 0, infants = 0): string {
  const parts = [`${adults} adult${adults === 1 ? '' : 's'}`];
  if (children) parts.push(`${children} child${children === 1 ? '' : 'ren'}`);
  if (infants) parts.push(`${infants} infant${infants === 1 ? '' : 's'}`);
  return parts.join(', ');
}

export function App() {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<SearchFormValues>();

  const [view, setView] = useState<View>('search');
  const [auth, setAuth] = useState<AuthStatus | null>(null);
  const [wallet, setWallet] = useState<WalletBalance | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [walletVersion, setWalletVersion] = useState(0);
  const [bookingsVersion, setBookingsVersion] = useState(0);
  const [waf, setWaf] = useState<WafStatus | null>(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [meta, setMeta] = useState<SearchMeta | null>(null);
  const [maxStops, setMaxStops] = useState<MaxStops>(null);
  const [drawerDay, setDrawerDay] = useState<DayResult | null>(null);
  const [booking, setBooking] = useState<BookingTarget | null>(null);
  const [prefs, setPrefs] = useState<AccountPrefs | null>(null);
  const [history, setHistory] = useState<JobSummary[] | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const routeRef = useRef('');
  const narrow = useNarrow();

  // A search is in flight until the server reports it finished, failed or was cancelled.
  const running = jobId !== null && (job === null || job.status === 'running');

  const recentOrigins: AirportOption[] = (prefs?.origins ?? []).map(fromPref);
  const recentDestinations: AirportOption[] = (prefs?.destinations ?? []).map(fromPref);

  const openLogin = useCallback(() => setLoginOpen(true), []);

  const refreshWallet = useCallback(() => {
    api
      .walletBalance()
      .then((w) => {
        setWallet(w);
        setWalletError(null);
      })
      .catch((e) => {
        setWallet(null);
        setWalletError(errorText(e));
      });
  }, []);

  const loadHistory = useCallback(() => {
    api.listSearches().then(setHistory).catch(() => setHistory([]));
  }, []);

  /**
   * Restore what this account searched last time. Because the server keys these
   * to the etravel account, the same login on a phone and a laptop sees the same
   * airports. Returns true when it filled the form.
   */
  const loadPrefs = useCallback(async () => {
    try {
      const p = await api.prefs();
      setPrefs(p);
      const origin = p.origins[0] ? fromPref(p.origins[0]) : null;
      const destination = p.destinations[0] ? fromPref(p.destinations[0]) : null;
      if (!origin && !destination) return false;
      const current = form.getFieldsValue(['origin', 'destination']);
      form.setFieldsValue({
        origin: current.origin ?? origin,
        destination: current.destination ?? destination,
        ...(p.lastSearch
          ? {
              type: p.lastSearch.type,
              cabin: p.lastSearch.cabinClass as SearchFormValues['cabin'],
              adults: p.lastSearch.adults,
              ...(p.lastSearch.stayLengthMin ? { stayMin: p.lastSearch.stayLengthMin } : {}),
              ...(p.lastSearch.stayLengthMax ? { stayMax: p.lastSearch.stayLengthMax } : {}),
            }
          : {}),
      });
      return true;
    } catch (err) {
      if (err instanceof ApiRequestError && err.requiresLogin) openLogin();
      return false;
    }
  }, [form, openLogin]);

  // Fallback for a brand-new account: the etravel default airports (MJI → YUL).
  const loadDefaultAirports = useCallback(async () => {
    try {
      const list = rawAirportList(await api.defaultAirports());
      const find = (pred: (a: Record<string, unknown>) => boolean) => {
        const raw = list.find(pred);
        return raw ? toAirportOption(raw) : null;
      };
      const code = (a: Record<string, unknown>) => a.code ?? a.iataCode;
      const mji = find((a) => code(a) === 'MJI');
      const yul = find((a) => code(a) === 'YUL') ?? find((a) => /trudeau/i.test(String(a.name ?? '')));
      const current = form.getFieldsValue(['origin', 'destination']);
      form.setFieldsValue({
        origin: current.origin ?? mji,
        destination: current.destination ?? yul,
      });
    } catch (err) {
      if (err instanceof ApiRequestError && err.requiresLogin) openLogin();
    }
  }, [form, openLogin]);

  // Pull account status (and wallet) from the backend.
  const refreshAuth = useCallback(async () => {
    try {
      const s = await api.authStatus();
      setAuth(s);
      if (s.loggedIn) {
        refreshWallet();
        loadHistory();
      } else {
        setWallet(null);
        setHistory(null);
      }
      return s.loggedIn;
    } catch {
      setAuth({ loggedIn: false });
      return false;
    }
  }, [refreshWallet, loadHistory]);

  // Boot: if signed in, restore this account's airports (falling back to the
  // etravel defaults on a first visit); otherwise prompt sign-in.
  useEffect(() => {
    void (async () => {
      const loggedIn = await refreshAuth();
      if (!loggedIn) {
        setLoginOpen(true);
        return;
      }
      if (!(await loadPrefs())) void loadDefaultAirports();
    })();
  }, [refreshAuth, loadPrefs, loadDefaultAirports]);

  // Poll this account's WAF rate-limit state so the retry banner stays current.
  useEffect(() => {
    if (!auth?.loggedIn) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const w = await api.waf();
        if (!cancelled) setWaf(w);
      } catch {
        /* transient — keep the last known state */
      }
      if (!cancelled) timer = setTimeout(tick, 1200);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [auth?.loggedIn]);

  // Poll the running job.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const j = await api.getJob(jobId);
        if (cancelled) return;
        setJob(j);
        if (j.status === 'running') timer = setTimeout(tick, POLL_MS);
        else loadHistory();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiRequestError && err.status === 404) {
          message.error('That search no longer exists.');
          setJobId(null);
          return;
        }
        timer = setTimeout(tick, POLL_MS);
      }
    };
    void tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, loadHistory, message]);

  const onSubmit = async (v: SearchFormValues) => {
    if (!v.origin || !v.destination) {
      message.error('Pick a From and To airport.');
      return;
    }
    const year = v.month.year();
    const month = v.month.month() + 1;
    const stayMin = Math.max(1, v.stayMin || 7);
    const stayMax = Math.max(stayMin, v.stayMax || stayMin);

    const body: SearchRequestBody = {
      origin: v.origin.code,
      destination: v.destination.code,
      origins: v.origin.codes,
      destinations: v.destination.codes,
      year,
      month,
      type: v.type,
      cabinClass: v.cabin,
      adults: v.adults,
      children: v.children ?? 0,
      infants: v.infants ?? 0,
      originAirport: toPrefInput(v.origin),
      destinationAirport: toPrefInput(v.destination),
    };
    if (v.type === 'ROUNDTRIP') {
      body.stayLengthMin = stayMin;
      body.stayLengthMax = stayMax;
      body.stayLength = stayMin;
      if (v.returnMonth) {
        // A return month earlier than the outbound month means next year.
        body.returnMonth = v.returnMonth;
        body.returnYear = v.returnMonth >= month ? year : year + 1;
      }
    }

    setSubmitting(true);
    try {
      const { jobId: id } = await api.startSearch(body);
      setCancelling(false);
      routeRef.current = `${v.origin.code} → ${v.destination.code}`;
      setMeta({
        title: `${airportLabel(v.origin)} ${v.type === 'ROUNDTRIP' ? '⇆' : '→'} ${airportLabel(v.destination)} · ${MONTHS[month - 1]} ${year}`,
        pax: paxLabel(v.adults, v.children, v.infants),
        cabin: CABIN_LABELS[v.cabin],
        startedAt: new Date().toISOString(),
      });
      setJob(null);
      setDrawerDay(null);
      setJobId(id);
      setView('search');
      // Pick up the airport we just used, so it leads the list next time.
      void api.prefs().then(setPrefs).catch(() => {});
      loadHistory();
    } catch (err) {
      if (err instanceof ApiRequestError && err.requiresLogin) openLogin();
      else message.error(errorText(err), 6);
    } finally {
      setSubmitting(false);
    }
  };

  /** Stop the running search. Partial results stay on screen; Search re-enables. */
  const onCancel = async () => {
    if (!jobId) return;
    setCancelling(true);
    try {
      const stopped = await api.cancelSearch(jobId);
      setJob(stopped);
      setJobId(null);
      message.info('Search cancelled.');
      loadHistory();
    } catch (err) {
      if (err instanceof ApiRequestError && err.requiresLogin) openLogin();
      else message.error(errorText(err), 6);
    } finally {
      setCancelling(false);
    }
  };

  /** Reopen a past search: its results are on the server, so it shows at once. */
  const openHistory = (j: JobSummary) => {
    const p = j.params;
    routeRef.current = `${p.origin} → ${p.destination}`;
    setMeta({
      title: summaryTitle(j),
      pax: paxLabel(p.adults, p.children, p.infants),
      cabin: CABIN_LABELS[p.cabinClass as keyof typeof CABIN_LABELS] ?? String(p.cabinClass),
      startedAt: j.createdAt,
    });
    setJob(null);
    setDrawerDay(null);
    setJobId(j.id);
    setView('search');
  };

  const deleteHistory = async (j: JobSummary) => {
    try {
      await api.deleteSearch(j.id);
      if (j.id === jobId) {
        setJobId(null);
        setJob(null);
        setMeta(null);
      }
      loadHistory();
    } catch (err) {
      message.error(errorText(err));
    }
  };

  const onSignOut = async () => {
    try {
      await api.logout();
    } finally {
      setAuth({ loggedIn: false });
      setWallet(null);
      setJob(null);
      setJobId(null);
      setMeta(null);
      setPrefs(null);
      setHistory(null);
      setView('search');
      message.success('Signed out.');
      setLoginOpen(true);
    }
  };

  const onOpenDay = (day: DayResult) => {
    if (!day.flights.length) {
      message.info(day.status === 'error' ? 'Search failed for this day.' : 'No flights for this day.');
      return;
    }
    setDrawerDay(day);
  };

  const onBook = (flight: JobFlight, day: DayResult) => {
    if (!job) return;
    setBooking({ flight, day, params: job.params });
  };

  const walletChanged = () => {
    refreshWallet();
    setWalletVersion((v) => v + 1);
  };

  const searchPanel = (
    <>
      <Card size="small" variant="borderless" styles={{ body: { padding: 8 } }}>
        <SearchForm
          form={form}
          submitting={submitting}
          onSubmit={(v) => void onSubmit(v)}
          onRequiresLogin={openLogin}
          running={running}
          cancelling={cancelling}
          onCancel={() => void onCancel()}
          recentOrigins={recentOrigins}
          recentDestinations={recentDestinations}
        />
      </Card>
      {auth?.loggedIn && (
        <div className="sider-section">
          <Typography.Text type="secondary" className="sider-section__title">
            Recent searches
          </Typography.Text>
          <RecentSearches items={history} currentId={jobId ?? job?.id ?? null} onOpen={openHistory} onDelete={(j) => void deleteHistory(j)} />
        </div>
      )}
    </>
  );

  const navItems = [
    { key: 'search', label: 'Search', icon: <CalendarOutlined /> },
    { key: 'bookings', label: 'Bookings', icon: <UserOutlined /> },
    { key: 'wallet', label: 'Wallet', icon: <WalletOutlined /> },
    { key: 'passengers', label: 'Passengers', icon: <TeamOutlined /> },
  ];

  return (
    <Layout className="app-layout">
      <Layout.Header className="app-header">
        <div className="brand">
          <img src="/logo.png" alt="eTravel" className="brand__logo" />
          <Typography.Text strong style={{ fontSize: 16 }} className="brand__name">
            Monthly Search
          </Typography.Text>
        </div>
        {auth?.loggedIn && (
          <Tabs className="nav" activeKey={view} onChange={(k) => setView(k as View)} items={navItems.map((n) => ({ key: n.key, label: <span>{n.icon} {n.label}</span> }))} />
        )}
        <Space size={8} className="header-right">
          {auth?.loggedIn ? (
            <>
              <Tooltip title={walletError ?? 'Wallet balance — open the wallet'}>
                <Button className="wallet-chip" icon={<WalletOutlined />} onClick={() => setView('wallet')}>
                  {wallet ? (
                    <>
                      {fmtMoney(wallet.balance)} <small>{wallet.currency}</small>
                    </>
                  ) : walletError ? (
                    '—'
                  ) : (
                    '…'
                  )}
                </Button>
              </Tooltip>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddFundsOpen(true)}>
                Add funds
              </Button>
              <Dropdown
                menu={{
                  items: [
                    { key: 'email', label: auth.email, disabled: true },
                    { type: 'divider' },
                    ...(auth.proxyAdmin ? [{ key: 'settings', icon: <SettingOutlined />, label: 'Relay settings', onClick: () => setSettingsOpen(true) }] : []),
                    { key: 'site', icon: <ExportOutlined />, label: 'Open etravel.ly', onClick: () => window.open('https://etravel.ly/flights', '_blank', 'noreferrer') },
                    { key: 'out', icon: <LogoutOutlined />, label: 'Sign out', onClick: () => void onSignOut() },
                  ],
                }}
              >
                <Button icon={<UserOutlined />}>{auth.email?.split('@')[0]}</Button>
              </Dropdown>
            </>
          ) : (
            <Button type="primary" icon={<UserOutlined />} onClick={openLogin}>
              Sign in
            </Button>
          )}
        </Space>
      </Layout.Header>

      {view === 'search' && (
        <Layout>
          {!narrow && (
            <Layout.Sider width={380} className="app-sider" theme="light">
              {searchPanel}
            </Layout.Sider>
          )}

          <Layout.Content className="app-content">
            <WafBanner status={waf} />
            {narrow && <div className="search-stack">{searchPanel}</div>}
            <ResultsPanel job={job} meta={meta} maxStops={maxStops} onMaxStops={setMaxStops} onOpenDay={onOpenDay} onBook={onBook} />
          </Layout.Content>
        </Layout>
      )}

      {view === 'bookings' && (
        <Layout.Content className="app-content">
          <WafBanner status={waf} />
          <BookingsPage email={auth?.email ?? null} version={bookingsVersion} onWalletChanged={walletChanged} />
        </Layout.Content>
      )}
      {view === 'wallet' && (
        <Layout.Content className="app-content">
          <WafBanner status={waf} />
          <WalletPage
            wallet={wallet}
            walletError={walletError}
            onRefreshWallet={refreshWallet}
            onAddFunds={() => setAddFundsOpen(true)}
            onTransfer={() => setTransferOpen(true)}
            version={walletVersion}
          />
        </Layout.Content>
      )}
      {view === 'passengers' && (
        <Layout.Content className="app-content">
          <PassengersPage />
        </Layout.Content>
      )}

      <DayDrawer day={drawerDay} maxStops={maxStops} route={routeRef.current} onClose={() => setDrawerDay(null)} onBook={onBook} />

      <BookingDrawer
        target={booking}
        wallet={wallet}
        onClose={() => {
          setBooking(null);
        }}
        onBooked={() => {
          walletChanged();
          setBookingsVersion((v) => v + 1);
        }}
        onAddFunds={() => setAddFundsOpen(true)}
        onRequiresLogin={openLogin}
      />

      <AddFundsModal open={addFundsOpen} currency={wallet?.currency ?? 'LYD'} onClose={() => setAddFundsOpen(false)} onChanged={walletChanged} />
      <TransferModal open={transferOpen} wallet={wallet} onClose={() => setTransferOpen(false)} onChanged={walletChanged} />

      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <LoginModal
        open={loginOpen}
        onCancel={() => setLoginOpen(false)}
        onSuccess={() => {
          setLoginOpen(false);
          void refreshAuth();
          void (async () => {
            if (!(await loadPrefs())) void loadDefaultAirports();
          })();
        }}
      />
    </Layout>
  );
}
